import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Card } from '../../../components/ui/Card';
import { RefreshCw, ChevronDown, ChevronRight, Users, Target, Heart, Calendar } from 'lucide-react';
import AccountSwitcher from '../../../components/AccountSwitcher';
import { useAccounts } from '../../../context/AccountContext';
import DatePicker from 'react-datepicker';

const API_BASE = 'https://api.glowytics.com';
const DEMO_BRAND_ID = 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4';

const demoPayload = {
  totals: { spend: 1523400, revenue: 6940000, purchases: 1140 },
  age: [
    { label: '18-24', spend: 210000, revenue: 830000, purchases: 152 },
    { label: '25-34', spend: 430000, revenue: 2420000, purchases: 388 },
    { label: '35-44', spend: 360000, revenue: 2000000, purchases: 310 },
    { label: '45-54', spend: 240000, revenue: 1020000, purchases: 168 },
    { label: '55-64', spend: 180000, revenue: 510000, purchases: 84 },
    { label: '65+', spend: 103400, revenue: 160000, purchases: 38 }
  ],
  gender: [
    { label: 'female', spend: 820000, revenue: 4360000, purchases: 720 },
    { label: 'male', spend: 620000, revenue: 2330000, purchases: 380 },
    { label: 'unknown', spend: 83000, revenue: 250000, purchases: 40 }
  ],
  interestEstimated: {
    method: 'adset-targeting',
    rows: [
      { label: 'Fashion', spend: 240000, revenue: 1340000, purchases: 215 },
      { label: 'Online Shopping', spend: 210000, revenue: 1180000, purchases: 195 },
      { label: 'Shoes', spend: 160000, revenue: 720000, purchases: 120 },
      { label: 'Beauty & Skincare', spend: 140000, revenue: 530000, purchases: 88 },
      { label: 'Sportswear', spend: 120000, revenue: 410000, purchases: 70 },
      { label: 'Luxury Goods', spend: 95000, revenue: 620000, purchases: 32 }
    ]
  }
};

const demoCampaigns = [
  {
    id: '1', name: 'Brand Awareness — Spring Collection',
    totalSpend: 420000, revenue: 1890000, purchases: 312, impressions: 2450000, clicks: 38000,
    age: [
      { label: '18-24', spend: 75000, revenue: 270000 }, { label: '25-34', spend: 140000, revenue: 720000 },
      { label: '35-44', spend: 105000, revenue: 510000 }, { label: '45-54', spend: 60000, revenue: 240000 },
      { label: '55-64', spend: 28000, revenue: 110000 }, { label: '65+', spend: 12000, revenue: 40000 }
    ],
    gender: [{ label: 'female', spend: 240000, revenue: 1240000 }, { label: 'male', spend: 155000, revenue: 580000 }, { label: 'unknown', spend: 25000, revenue: 70000 }],
    interests: [{ label: 'Fashion', spend: 120000, revenue: 620000 }, { label: 'Online Shopping', spend: 95000, revenue: 480000 }, { label: 'Shoes', spend: 80000, revenue: 360000 }, { label: 'Sportswear', spend: 65000, revenue: 220000 }]
  },
  {
    id: '2', name: 'Retargeting — Cart Abandoners',
    totalSpend: 380000, revenue: 2650000, purchases: 485, impressions: 1800000, clicks: 42000,
    age: [
      { label: '18-24', spend: 50000, revenue: 280000 }, { label: '25-34', spend: 120000, revenue: 920000 },
      { label: '35-44', spend: 100000, revenue: 780000 }, { label: '45-54', spend: 70000, revenue: 450000 },
      { label: '55-64', spend: 30000, revenue: 170000 }, { label: '65+', spend: 10000, revenue: 50000 }
    ],
    gender: [{ label: 'female', spend: 210000, revenue: 1620000 }, { label: 'male', spend: 148000, revenue: 950000 }, { label: 'unknown', spend: 22000, revenue: 80000 }],
    interests: [{ label: 'Online Shopping', spend: 110000, revenue: 880000 }, { label: 'Beauty & Skincare', spend: 85000, revenue: 540000 }, { label: 'Luxury Goods', spend: 70000, revenue: 460000 }]
  },
  {
    id: '3', name: 'Lookalike — High-Value Purchasers',
    totalSpend: 350000, revenue: 1420000, purchases: 198, impressions: 3100000, clicks: 29000,
    age: [
      { label: '18-24', spend: 40000, revenue: 130000 }, { label: '25-34', spend: 100000, revenue: 460000 },
      { label: '35-44', spend: 95000, revenue: 430000 }, { label: '45-54', spend: 65000, revenue: 240000 },
      { label: '55-64', spend: 35000, revenue: 110000 }, { label: '65+', spend: 15000, revenue: 50000 }
    ],
    gender: [{ label: 'female', spend: 195000, revenue: 880000 }, { label: 'male', spend: 140000, revenue: 480000 }, { label: 'unknown', spend: 15000, revenue: 60000 }],
    interests: [{ label: 'Fashion', spend: 90000, revenue: 420000 }, { label: 'Shoes', spend: 75000, revenue: 330000 }, { label: 'Online Shopping', spend: 70000, revenue: 290000 }, { label: 'Luxury Goods', spend: 55000, revenue: 380000 }]
  },
  {
    id: '4', name: 'Engagement — New Arrivals',
    totalSpend: 270000, revenue: 980000, purchases: 145, impressions: 1950000, clicks: 22000,
    age: [
      { label: '18-24', spend: 55000, revenue: 150000 }, { label: '25-34', spend: 80000, revenue: 320000 },
      { label: '35-44', spend: 60000, revenue: 280000 }, { label: '45-54', spend: 40000, revenue: 150000 },
      { label: '55-64', spend: 25000, revenue: 60000 }, { label: '65+', spend: 10000, revenue: 20000 }
    ],
    gender: [{ label: 'female', spend: 160000, revenue: 620000 }, { label: 'male', spend: 95000, revenue: 320000 }, { label: 'unknown', spend: 15000, revenue: 40000 }],
    interests: [{ label: 'Fashion', spend: 80000, revenue: 320000 }, { label: 'Beauty & Skincare', spend: 60000, revenue: 240000 }, { label: 'Sportswear', spend: 50000, revenue: 180000 }]
  }
];

function MiniBar({ rows, totalSpend, totalRevenue, spendColor = '#3b82f6', revenueColor = '#10b981' }) {
  if (!rows?.length) return <p className="text-xs text-gray-400 italic">No data</p>;
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-400 font-medium pb-1 border-b border-gray-100">
        <span className="w-20" />
        <div className="flex-1 flex items-center gap-3">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ backgroundColor: spendColor }} /> Spend</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ backgroundColor: revenueColor }} /> Revenue</span>
        </div>
      </div>
      {rows.map((r) => {
        const spendPct = totalSpend > 0 ? (r.spend / totalSpend * 100) : 0;
        const revPct = totalRevenue > 0 ? ((r.revenue || 0) / totalRevenue * 100) : 0;
        return (
          <div key={r.label} className="flex items-center gap-2">
            <span className="text-[11px] text-gray-600 w-20 truncate">{r.label}</span>
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2.5 bg-gray-100 rounded overflow-hidden">
                  <div className="h-full rounded transition-all" style={{ width: `${Math.max(spendPct, 1)}%`, backgroundColor: spendColor }} />
                </div>
                <span className="text-[10px] font-medium text-gray-600 w-10 text-right">{spendPct.toFixed(1)}%</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2.5 bg-gray-100 rounded overflow-hidden">
                  <div className="h-full rounded transition-all" style={{ width: `${Math.max(revPct, 1)}%`, backgroundColor: revenueColor }} />
                </div>
                <span className="text-[10px] font-medium text-gray-600 w-10 text-right">{revPct.toFixed(1)}%</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CampaignAudienceCard({ campaign, currency }) {
  const [open, setOpen] = useState(false);
  const totalSpend = campaign.totalSpend || 0;
  const totalRevenue = campaign.revenue || 0;
  const roas = totalSpend > 0 ? (totalRevenue / totalSpend).toFixed(2) : '—';
  const cpa = campaign.purchases > 0 ? (totalSpend / campaign.purchases) : null;
  const fmtK = (n) => {
    if (!n) return '0';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return String(n);
  };
  return (
    <Card className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 border border-blue-100">
            <Target className="w-4 h-4 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900 truncate">{campaign.name}</p>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              <span className="text-xs text-gray-500">Spend: <strong className="text-gray-700">{formatCurrency(campaign.totalSpend, currency)}</strong></span>
              <span className="text-xs text-emerald-600">Revenue: <strong>{formatCurrency(campaign.revenue, currency)}</strong></span>
              <span className="text-xs text-purple-600">ROAS: <strong>{roas}x</strong></span>
              <span className="text-xs text-orange-600">CPA: <strong>{cpa != null ? formatCurrency(cpa, currency) : '—'}</strong></span>
              <span className="text-xs text-gray-400">Purchases: <strong className="text-gray-600">{fmtK(campaign.purchases)}</strong></span>
              <span className="text-xs text-gray-400">Impr: <strong className="text-gray-600">{fmtK(campaign.impressions)}</strong></span>
              <span className="text-xs text-gray-400">Clicks: <strong className="text-gray-600">{fmtK(campaign.clicks)}</strong></span>
            </div>
          </div>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="border-t border-gray-100 p-4 bg-gray-50/50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <Users className="w-3.5 h-3.5 text-blue-500" />
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Age</span>
              </div>
              <MiniBar rows={campaign.age} totalSpend={totalSpend} totalRevenue={totalRevenue} />
            </div>
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <Heart className="w-3.5 h-3.5 text-pink-500" />
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Gender</span>
              </div>
              <MiniBar rows={campaign.gender} totalSpend={totalSpend} totalRevenue={totalRevenue} spendColor="#ec4899" revenueColor="#10b981" />
            </div>
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <Target className="w-3.5 h-3.5 text-purple-500" />
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Interests (Est.)</span>
              </div>
              <MiniBar rows={campaign.interests} totalSpend={totalSpend} totalRevenue={totalRevenue} spendColor="#8b5cf6" revenueColor="#10b981" />
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

const DATE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7', label: 'Last 7 Days' },
  { value: 'last30', label: 'Last 30 Days' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'custom', label: 'Custom Range' }
];

const formatCurrency = (value, currency) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return '0';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'TRY',
      maximumFractionDigits: 0
    }).format(number);
  } catch (e) {
    return Math.round(number).toLocaleString('en-US');
  }
};

function SimpleTable({ title, subtitle, rows, emptyText, currency }) {
  const totals = (rows || []).reduce(
    (acc, r) => ({
      spend: acc.spend + (Number(r.spend) || 0),
      revenue: acc.revenue + (Number(r.revenue) || 0),
      purchases: acc.purchases + (Number(r.purchases) || 0)
    }),
    { spend: 0, revenue: 0, purchases: 0 }
  );
  const totalSpend = totals.spend || 1;
  const totalRevenue = totals.revenue || 1;
  const overallRoas = totals.spend > 0 ? (totals.revenue / totals.spend).toFixed(2) : '—';
  const overallCpa = totals.purchases > 0 ? formatCurrency(totals.spend / totals.purchases, currency) : '—';
  return (
    <Card className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="p-6 bg-white border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        {subtitle ? <p className="text-sm text-gray-500 mt-1">{subtitle}</p> : null}
      </div>
      <div className="bg-white">
        {!rows || rows.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-gray-500">{emptyText}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Segment</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Spend</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">ROAS</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CPA</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Spend % / Rev %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => {
                  const sPct = (row.spend / totalSpend) * 100;
                  const rPct = ((row.revenue || 0) / totalRevenue) * 100;
                  const roas = row.spend > 0 ? ((row.revenue || 0) / row.spend).toFixed(2) : '—';
                  const cpa = row.purchases > 0 ? formatCurrency(row.spend / row.purchases, currency) : '—';
                  const revColor = (row.revenue || 0) > 0 ? 'text-emerald-700' : 'text-gray-400';
                  return (
                    <tr key={row.label}>
                      <td className="px-4 py-3 text-sm text-gray-900">{row.label}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right tabular-nums">{formatCurrency(row.spend, currency)}</td>
                      <td className={`px-4 py-3 text-sm text-right tabular-nums ${revColor}`}>{formatCurrency(row.revenue || 0, currency)}</td>
                      <td className="px-4 py-3 text-sm text-purple-600 text-right tabular-nums">{roas !== '—' ? `${roas}x` : '—'}</td>
                      <td className="px-4 py-3 text-sm text-orange-600 text-right tabular-nums">{cpa}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2 min-w-[160px]">
                          <div className="flex-1 space-y-1">
                            <div className="h-1.5 bg-gray-100 rounded overflow-hidden">
                              <div className="h-full bg-blue-500" style={{ width: `${Math.max(sPct, 1)}%` }} />
                            </div>
                            <div className="h-1.5 bg-gray-100 rounded overflow-hidden">
                              <div className="h-full bg-emerald-500" style={{ width: `${Math.max(rPct, 1)}%` }} />
                            </div>
                          </div>
                          <div className="text-[10px] text-gray-500 tabular-nums w-20 text-right leading-tight">
                            <div className="text-blue-600">{sPct.toFixed(1)}%</div>
                            <div className="text-emerald-600">{rPct.toFixed(1)}%</div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50 border-t border-gray-200">
                <tr>
                  <td className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase">Total</td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right tabular-nums">{formatCurrency(totals.spend, currency)}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-emerald-700 text-right tabular-nums">{formatCurrency(totals.revenue, currency)}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-purple-700 text-right tabular-nums">{overallRoas !== '—' ? `${overallRoas}x` : '—'}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-orange-700 text-right tabular-nums">{overallCpa}</td>
                  <td className="px-4 py-3 text-xs text-gray-400 text-right">
                    <span className="text-blue-600">Spend</span> / <span className="text-emerald-600">Revenue</span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}

export default function MetaAudienceInsights() {
  const { selectedBrand } = useAuth();
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('META_ADS');
  const [dateRange, setDateRange] = useState('last30');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [campaignData, setCampaignData] = useState([]);
  const [campaignLoading, setCampaignLoading] = useState(false);

  const formatDateYMD = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

  const fetchCampaignInsights = useCallback(async () => {
    if (!selectedBrand?.id) return;
    setCampaignLoading(true);
    try {
      if (selectedBrand.id === DEMO_BRAND_ID) {
        setCampaignData(demoCampaigns);
        return;
      }
      const url = new URL(`${API_BASE}/api/meta-ads/audience-spend-by-campaign`);
      url.searchParams.set('dateRange', dateRange);
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        url.searchParams.set('startDate', formatDateYMD(customStartDate));
        url.searchParams.set('endDate', formatDateYMD(customEndDate));
      }
      const resp = await fetch(url.toString(), {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id }
      });
      if (resp.ok) {
        const d = await resp.json();
        setCampaignData(Array.isArray(d?.campaigns) ? d.campaigns : []);
      }
    } catch (_) {
      setCampaignData([]);
    } finally {
      setCampaignLoading(false);
    }
  }, [selectedBrand?.id, dateRange, customStartDate, customEndDate]);

  const fetchInsights = async () => {
    if (!selectedBrand?.id) return;
    setLoading(true);
    setError('');
    try {
      if (selectedBrand.id === DEMO_BRAND_ID) {
        setPayload(demoPayload);
        setLastUpdated(new Date());
        fetchCampaignInsights();
        return;
      }

      if (dateRange === 'custom' && (!customStartDate || !customEndDate)) {
        throw new Error('Please select a start and end date.');
      }

      const url = new URL(`${API_BASE}/api/meta-ads/audience-spend`);
      url.searchParams.set('status', 'ACTIVE');
      url.searchParams.set('dateRange', dateRange);
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        url.searchParams.set('startDate', formatDateYMD(customStartDate));
        url.searchParams.set('endDate', formatDateYMD(customEndDate));
      }
      const resp = await fetch(url.toString(), {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id }
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d?.error || d?.details || `HTTP ${resp.status}`);
      }
      const data = await resp.json().catch(() => ({}));
      setPayload({
        totals: data?.totals || { spend: 0 },
        age: Array.isArray(data?.age) ? data.age : [],
        gender: Array.isArray(data?.gender) ? data.gender : [],
        interestEstimated: data?.interestEstimated || { method: 'adset-targeting', rows: [] }
      });
      setLastUpdated(new Date());
      fetchCampaignInsights();
    } catch (e) {
      setError(e?.message || 'Failed to load Meta audience insights');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange === 'custom') {
      if (customStartDate && customEndDate) {
        fetchInsights();
      }
    } else {
      fetchInsights();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id, dateRange, customStartDate, customEndDate, selectedAccountId]);

  const summary = useMemo(() => {
    const spend = payload?.totals?.spend || 0;
    const revenue = payload?.totals?.revenue || 0;
    const purchases = payload?.totals?.purchases || 0;
    return {
      spend,
      revenue,
      purchases,
      roas: spend > 0 ? (revenue / spend) : 0,
      cpa: purchases > 0 ? (spend / purchases) : 0
    };
  }, [payload]);

  const currency = selectedBrand?.currency;

  return (
    <div className="p-6 space-y-6">
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        <div className="p-6 bg-white text-gray-900 border-b flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center border border-blue-100">
              <img src="/6033716.png" alt="Meta" className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl tracking-tight">Meta Audience Insights</h1>
              <p className="text-sm text-gray-500">
                Spend, revenue, ROAS, and CPA by age, gender, and interest (estimated).
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <AccountSwitcher platform="META_ADS" />
            <select
              value={dateRange}
              onChange={(e) => {
                setDateRange(e.target.value);
                if (e.target.value === 'custom') {
                  setCustomStartDate(null);
                  setCustomEndDate(null);
                }
              }}
              className="text-sm px-3 py-2 border border-gray-200 rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[140px]"
            >
              {DATE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {dateRange === 'custom' && (
              <div className="relative">
                <button
                  onClick={() => setIsCustomDateOpen(!isCustomDateOpen)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  <Calendar className="w-4 h-4" />
                  {customStartDate && customEndDate ? (
                    `${customStartDate.toLocaleDateString()} - ${customEndDate.toLocaleDateString()}`
                  ) : (
                    'Select Dates'
                  )}
                </button>
                {isCustomDateOpen && (
                  <div className="absolute right-0 mt-2 z-50">
                    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
                      <DatePicker
                        selected={customStartDate}
                        onChange={(dates) => {
                          const [start, end] = dates;
                          setCustomStartDate(start);
                          setCustomEndDate(end);
                          if (start && end) {
                            setIsCustomDateOpen(false);
                          }
                        }}
                        startDate={customStartDate}
                        endDate={customEndDate}
                        selectsRange
                        inline
                        maxDate={new Date()}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={fetchInsights}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl border border-gray-200 hover:bg-gray-50 shadow-sm"
              disabled={loading}
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <div className="text-xs text-gray-500 whitespace-nowrap">
              {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : ''}
            </div>
          </div>
        </div>
        {error && (
          <div className="px-6 py-4 bg-red-50 text-sm text-red-600 border-b border-red-100">
            {error}
          </div>
        )}
        {!error && loading && (
          <div className="px-6 py-10 bg-white text-sm text-gray-500 text-center">Loading Meta audience insights...</div>
        )}
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4 rounded-2xl border border-gray-100 shadow-sm">
          <div className="text-xs text-gray-500">Total Spend</div>
          <div className="text-xl font-semibold text-gray-900 mt-1">
            {formatCurrency(summary.spend, currency)}
          </div>
        </Card>
        <Card className="p-4 rounded-2xl border border-gray-100 shadow-sm">
          <div className="text-xs text-gray-500">Total Revenue</div>
          <div className="text-xl font-semibold text-emerald-600 mt-1">
            {formatCurrency(summary.revenue, currency)}
          </div>
        </Card>
        <Card className="p-4 rounded-2xl border border-gray-100 shadow-sm">
          <div className="text-xs text-gray-500">ROAS</div>
          <div className="text-xl font-semibold text-purple-600 mt-1">
            {summary.roas > 0 ? `${summary.roas.toFixed(2)}x` : '—'}
          </div>
        </Card>
        <Card className="p-4 rounded-2xl border border-gray-100 shadow-sm">
          <div className="text-xs text-gray-500">CPA</div>
          <div className="text-xl font-semibold text-orange-600 mt-1">
            {summary.cpa > 0 ? formatCurrency(summary.cpa, currency) : '—'}
          </div>
        </Card>
        <Card className="p-4 rounded-2xl border border-gray-100 shadow-sm">
          <div className="text-xs text-gray-500">Purchases</div>
          <div className="text-xl font-semibold text-gray-900 mt-1">
            {Math.round(summary.purchases).toLocaleString('en-US')}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SimpleTable
          title="Performance by Age"
          subtitle="Spend, revenue, ROAS & CPA per age bucket"
          rows={payload?.age || []}
          emptyText="No age breakdown available."
          currency={currency}
        />
        <SimpleTable
          title="Performance by Gender"
          subtitle="Spend, revenue, ROAS & CPA per gender"
          rows={payload?.gender || []}
          emptyText="No gender breakdown available."
          currency={currency}
        />
      </div>

      <div className="grid grid-cols-1 gap-6">
        <SimpleTable
          title="Performance by Interest (Estimated)"
          subtitle="Spend & revenue split equally across each ad set's targeted interests — directional, not exact"
          rows={payload?.interestEstimated?.rows || []}
          emptyText="No interest targeting found to estimate spend."
          currency={currency}
        />
      </div>

      {/* Campaign-Level Audience Breakdown */}
      <Card className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-6 bg-white border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Audience Insights by Campaign</h2>
          <p className="text-sm text-gray-500 mt-1">
            Spend vs. Revenue distribution across age, gender, and interests per campaign. Click to expand.
          </p>
        </div>
      </Card>
      {campaignLoading && (
        <div className="text-center py-8 text-sm text-gray-500">Loading campaign insights...</div>
      )}
      {!campaignLoading && campaignData.length === 0 && (
        <div className="text-center py-8 text-sm text-gray-400">No campaign-level audience data available.</div>
      )}
      <div className="space-y-3">
        {campaignData.map((c) => (
          <CampaignAudienceCard key={c.id} campaign={c} currency={currency} />
        ))}
      </div>
    </div>
  );
}
